import { z } from "zod";

/* ===========================================================================
 * US-E5.F1-0077 Export Report (CSV streaming R2) — @UC0048, Phase 9 Reports (read-only).
 *
 * POST /v1/funds/:id/reports/export — admin/treasurer/owner yêu cầu xuất báo cáo giao dịch quỹ
 *   theo khoảng thời gian → tạo report job status=pending, enqueue worker → 202 { reportId, status }.
 * GET /v1/funds/:id/reports/:reportId — poll trạng thái job + signedUrl khi done.
 *
 * READ-ONLY: chỉ đọc ledger (KHÔNG post). Worker stream batched cursor → CSV → R2 (không OOM).
 * Redaction: story chốt admin/treasurer/owner only; CSV chỉ chứa field giao dịch (KHÔNG PII member,
 * KHÔNG số dư private của người khác). Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

/** Định dạng YYYY-MM-DD (khớp filter transactions 0054). */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Trạng thái report job. pending→ (worker) →done | failed. */
export const REPORT_STATUS = {
  PENDING: "pending",
  DONE: "done",
  FAILED: "failed",
} as const;
export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS];
export const reportStatusSchema = z.enum([
  REPORT_STATUS.PENDING,
  REPORT_STATUS.DONE,
  REPORT_STATUS.FAILED,
]);

/** Định dạng xuất — CSV trước (PDF defer story). */
export const REPORT_FORMAT = { CSV: "csv" } as const;
export type ReportFormat = (typeof REPORT_FORMAT)[keyof typeof REPORT_FORMAT];
export const reportFormatSchema = z.enum([REPORT_FORMAT.CSV]);

/**
 * Body POST export — khoảng thời gian [from, to] (YYYY-MM-DD, UTC, inclusive cuối ngày). Cả hai
 * optional (vắng = toàn bộ). `.strict()` từ chối field lạ (C5). superRefine: from ≤ to nếu có cả hai.
 */
export const exportReportRequestSchema = z
  .object({
    from: z.string().regex(DATE_PATTERN).optional(),
    to: z.string().regex(DATE_PATTERN).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.from && v.to && v.from > v.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "from phải ≤ to.",
        path: ["from"],
      });
    }
  });
export type ExportReportRequest = z.infer<typeof exportReportRequestSchema>;

/** Response 202 khi tạo job — reportId để client poll + trạng thái ban đầu (pending). */
export const exportReportResponseSchema = z
  .object({
    reportId: z.string().uuid(),
    status: reportStatusSchema,
  })
  .strict();
export type ExportReportResponse = z.infer<typeof exportReportResponseSchema>;

/**
 * Response poll GET report. `signedUrl` chỉ có khi done (TTL — presigned R2). failed → error message
 * (KHÔNG lộ chi tiết PII/hạ tầng). rowCount báo số dòng dữ liệu đã xuất khi done.
 */
export const reportStatusResponseSchema = z
  .object({
    reportId: z.string().uuid(),
    status: reportStatusSchema,
    format: reportFormatSchema,
    from: z.string().regex(DATE_PATTERN).nullable(),
    to: z.string().regex(DATE_PATTERN).nullable(),
    signedUrl: z.string().nullable(),
    rowCount: z.number().int().nonnegative().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type ReportStatusResponse = z.infer<typeof reportStatusResponseSchema>;

/**
 * Cột CSV báo cáo giao dịch (thứ tự cố định — header khớp). Redaction: KHÔNG có cột PII member/số dư
 * người khác — chỉ field giao dịch ledger (id/loại/hướng/số tiền/tiền tệ/memo/thời gian).
 */
export const REPORT_CSV_COLUMNS = [
  "transaction_id",
  "type",
  "direction",
  "amount_minor",
  "currency",
  "memo",
  "created_at",
] as const;
export type ReportCsvColumn = (typeof REPORT_CSV_COLUMNS)[number];

/* ===========================================================================
 * US-E23.F1-0331-S09 (@UC9514) — EXPORT CẤP NHÓM.
 *
 * POST /v1/groups/:id/reports/export  ·  GET /v1/groups/:id/reports/:reportId
 *
 * 🔴 TÁI DÙNG ENGINE CẤP QUỸ: cùng bảng `report_jobs`, cùng worker `report-export`, cùng đường R2 +
 * presigned URL. Chỉ MỞ RỘNG scope + thêm `kind`. Engine export thứ hai sẽ nghĩa là hai chỗ áp
 * redaction — và chỗ nào quên áp thì rò số tiền của nhóm ra file tải về được.
 *
 * Figma đặt BA mục xuất ở cấp nhóm; cả ba trả về CÙNG shape response với export cấp quỹ (client
 * poll một kiểu duy nhất).
 * ========================================================================= */

/** Loại nội dung xuất ở cấp NHÓM. Whitelist đóng — mirror CHECK `report_jobs_kind_check`. */
export const GROUP_REPORT_KIND = {
  /** Lịch sử giao dịch gộp MỌI quỹ chưa lưu trữ của nhóm. */
  TRANSACTIONS: "transactions",
  /** Danh sách thành viên (không PII nhạy cảm — xem GROUP_MEMBERS_CSV_COLUMNS). */
  MEMBERS: "members",
  /** Báo cáo hoạt động: mỗi quỹ một dòng tổng hợp. */
  ACTIVITY: "activity",
} as const;
export type GroupReportKind = (typeof GROUP_REPORT_KIND)[keyof typeof GROUP_REPORT_KIND];
export const groupReportKindSchema = z.enum([
  GROUP_REPORT_KIND.TRANSACTIONS,
  GROUP_REPORT_KIND.MEMBERS,
  GROUP_REPORT_KIND.ACTIVITY,
]);

/**
 * Body POST export cấp nhóm. `kind` BẮT BUỘC — không default: xuất nhầm loại báo cáo cho cả nhóm là
 * lỗi im lặng (file tải về trông vẫn "hợp lệ"), nên bắt khai tường minh.
 * `from`/`to` chỉ có nghĩa với `kind='transactions'`; hai kind còn lại là ảnh chụp hiện trạng.
 */
export const exportGroupReportRequestSchema = z
  .object({
    kind: groupReportKindSchema,
    from: z.string().regex(DATE_PATTERN).optional(),
    to: z.string().regex(DATE_PATTERN).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.from && v.to && v.from > v.to) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "from phải ≤ to.", path: ["from"] });
    }
    if (v.kind !== GROUP_REPORT_KIND.TRANSACTIONS && (v.from || v.to)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // Nhận rồi lờ đi là cách chắc chắn nhất để người dùng tin file đã lọc theo ngày trong khi không.
        message: "Chỉ báo cáo giao dịch mới lọc theo khoảng thời gian.",
        path: ["from"],
      });
    }
  });
export type ExportGroupReportRequest = z.infer<typeof exportGroupReportRequestSchema>;

/**
 * Cột CSV danh sách thành viên. Redaction: KHÔNG email/số điện thoại/địa chỉ — file này chia sẻ được
 * ra ngoài nhóm bằng một cú kéo thả, và PII công dân VN không được rời hệ thống theo đường đó.
 */
export const GROUP_MEMBERS_CSV_COLUMNS = [
  "member_id",
  "display_name",
  "role",
  "status",
  "joined_at",
] as const;
export type GroupMembersCsvColumn = (typeof GROUP_MEMBERS_CSV_COLUMNS)[number];

/** Cột CSV báo cáo hoạt động: mỗi quỹ một dòng. Tiền là STRING minor (B1 no-float). */
export const GROUP_ACTIVITY_CSV_COLUMNS = [
  "fund_id",
  "fund_name",
  "currency",
  "transaction_count",
  "member_count",
] as const;
export type GroupActivityCsvColumn = (typeof GROUP_ACTIVITY_CSV_COLUMNS)[number];

/* ===========================================================================
 * US-E24.F10-0359-S01 (@UC9598) — GET /v1/groups/:id/reports · GET /v1/funds/:id/reports.
 *
 * VÌ SAO CÓ: export là ASYNC 202 — người dùng bấm, đóng app, mở lại. `reportId` chỉ nằm trong bộ
 * nhớ client, và đường đọc DUY NHẤT là `GET .../reports/:reportId` (poll theo id ĐÃ BIẾT). Kill app
 * trước khi client kịp persist ⇒ job vẫn chạy, file vẫn nằm trên R2, KHÔNG AI LẤY ĐƯỢC. Bấm export
 * lại ⇒ mỗi lần một job mới ⇒ R2 tích rác mà không ai biết có bao nhiêu bản đang treo.
 * Hai `@@index([fundId, createdAt])` / `([groupId, createdAt])` đã có sẵn — index chỉ có nghĩa cho
 * truy vấn LIST theo thời gian. Hạ tầng cho list đã chuẩn bị, chỉ thiếu endpoint.
 *
 * 🔴 KHÔNG `objectKey`, KHÔNG presigned URL trong list. `objectKey` là con trỏ R2 bền
 * ("lưu KEY bền, KHÔNG URL"). Link tải trong một response list = N link sống cùng lúc trong
 * log/cache client, TTL không kiểm soát được. Muốn tải ⇒ vẫn qua `GET .../reports/:reportId`.
 * List chỉ trả cờ `hasFile` — đủ để UI biết nút "Tải" có bật được không, và không hơn.
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const REPORT_JOBS_MAX_LIMIT = 50;
export const REPORT_JOBS_MAX_CURSOR_LENGTH = 512;

/**
 * Lưới quét link tải rò vào list (dùng trong test). Cố ý CHỈ bắt scheme http/https — hẹp và ít
 * dương-tính-giả, vì một guard hay báo động giả là guard sẽ bị tắt.
 */
export const REPORT_JOB_URL_SHAPE = /https?:\/\//;

/**
 * 1 dòng job trong danh sách — METADATA JOB, KHÔNG phải nội dung báo cáo ⇒ **0 trường tiền**.
 *
 * `error`: cột DB có comment "KHÔNG PII/hạ tầng"; worker ghi đúng một chuỗi hằng khi hỏng. Trả ra
 * là để người dùng biết bản xuất đó chết chứ không phải đang treo — nhưng test khoá lại: nội dung
 * không được mang đường dẫn file/hostname/stack.
 * `hasFile`: thay cho `objectKey`. Trạng thái `done` mà file rỗng là ca có thật (job hỏng nửa
 * chừng), nên cờ này KHÔNG suy từ `status` — nó là sự thật riêng.
 */
export const reportJobListItemSchema = z
  .object({
    reportId: z.string().uuid(),
    status: reportStatusSchema,
    /** Whitelist ĐÓNG, mirror CHECK `report_jobs_kind_check`. Job cấp quỹ luôn `transactions`. */
    kind: groupReportKindSchema,
    format: reportFormatSchema,
    from: z.string().regex(DATE_PATTERN).nullable(),
    to: z.string().regex(DATE_PATTERN).nullable(),
    rowCount: z.number().int().nonnegative().nullable(),
    error: z.string().nullable(),
    hasFile: z.boolean(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type ReportJobListItem = z.infer<typeof reportJobListItemSchema>;

/** Envelope cursor chuẩn repo. 0 job ⇒ `data: []` (KHÔNG 404 — "chưa xuất lần nào" là sự thật hợp lệ). */
export const reportJobsListResponseSchema = z
  .object({
    data: z.array(reportJobListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type ReportJobsListResponse = z.infer<typeof reportJobsListResponseSchema>;

/**
 * Query: cursor + limit + filter `status`.
 * 🔴 `status` rác ⇒ 400 tại biên, KHÔNG rơi vào nhánh "không lọc". Nhận rồi lờ đi là cách chắc chắn
 * nhất để người dùng tin danh sách đã lọc trong khi không.
 */
export const reportJobsListQuerySchema = z.object({
  cursor: z.string().max(REPORT_JOBS_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(REPORT_JOBS_MAX_LIMIT).optional(),
  status: reportStatusSchema.optional(),
});

export type ReportJobsListQuery = z.infer<typeof reportJobsListQuerySchema>;
